import { useState, useEffect } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { Mail, Lock, Eye, EyeOff, LogIn, Sparkles } from 'lucide-react'
import { useAuth } from '../context/AuthContext'

export default function Login() {
  const { user, signIn, loading, error } = useAuth()
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [debugInfo, setDebugInfo] = useState('')

  // Debug: vérifier l'état des services Appwrite
  useEffect(() => {
    const checkServices = () => {
      const services = {
        appwriteAuth: !!window.appwriteAuth,
        appwriteClient: !!(window.appwriteAuth?.client),
        appwriteAccount: !!(window.appwriteAuth?.account),
        claudeAPI: !!window.claudeAPI,
        fileHandler: !!window.FileHandler
      }
      setDebugInfo(JSON.stringify(services, null, 2))
      console.log('🔍 État des services Appwrite:', services)
    }

    checkServices()
    const interval = setInterval(checkServices, 2000)
    return () => clearInterval(interval)
  }, [])

  // Rediriger si déjà connecté
  if (user && !loading) {
    console.log('✅ Utilisateur connecté, redirection vers dashboard')
    return <Navigate to="/" replace />
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    console.log('🚀 === DÉBUT CONNEXION APPWRITE ===')
    console.log('📧 Email:', formData.email)
    console.log('🔧 Services disponibles:', {
      appwriteAuth: !!window.appwriteAuth,
      account: !!(window.appwriteAuth?.account)
    })

    try {
      const result = await signIn(formData.email, formData.password)
      
      console.log('📋 Résultat final:', result)
      
      if (!result.success) {
        console.error('❌ Échec final:', result.error)
      }
    } catch (error) {
      console.error('❌ Erreur inattendue:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 relative overflow-hidden">
      
      <div className="absolute inset-0 bg-gradient-to-br from-pink-900/20 via-purple-900/20 to-blue-900/20"></div>
      
      <div className="max-w-md w-full space-y-8 relative z-10">
        
        {/* Hero Section */}
        <div className="glass-card text-center p-8">
          <div className="animate-float">
            <div className="w-24 h-24 mx-auto bg-gradient-to-r from-pink-500 to-blue-500 rounded-3xl flex items-center justify-center mb-8 shadow-2xl relative group">
              <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-blue-500 rounded-3xl blur-xl opacity-50 group-hover:opacity-75 animate-pulse"></div>
              <Sparkles className="w-12 h-12 text-white relative z-10" />
            </div>
          </div>
          
          <h1 className="text-5xl font-bold bg-gradient-to-r from-pink-400 via-purple-400 to-blue-400 bg-clip-text text-transparent leading-tight mb-4">
            CV Screening Pro
          </h1>
          <p className="text-xl text-gray-300 mb-2">Intelligence Artificielle pour le Recrutement</p>
          <p className="text-sm text-gray-400">Connexion à votre espace professionnel</p>
        </div>

        {/* Formulaire de connexion */}
        <div className="glass-card p-8">
          <form className="space-y-6" onSubmit={handleSubmit}>
            
            {/* Message d'erreur */}
            {error && (
              <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-300 text-sm text-center">
                ❌ {error}
              </div>
            )}

            {/* Email */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">
                Adresse email professionnelle
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400 group-focus-within:text-blue-400 transition-colors" />
                </div>
                <input
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="block w-full pl-12 pr-4 py-4 border border-gray-600 rounded-xl bg-gray-800/50 backdrop-blur-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="votre@entreprise.com"
                />
              </div>
            </div>

            {/* Mot de passe */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-300">
                Mot de passe
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-blue-400 transition-colors" />
                </div>
                <input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="block w-full pl-12 pr-12 py-4 border border-gray-600 rounded-xl bg-gray-800/50 backdrop-blur-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Votre mot de passe sécurisé"
                />
                <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-gray-400 hover:text-gray-300 transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            </div>

            {/* Bouton de connexion */}
            <button
              type="submit"
              disabled={isSubmitting || !formData.email || !formData.password}
              className="w-full flex items-center justify-center py-4 px-4 bg-gradient-to-r from-pink-500 to-blue-500 text-white font-semibold rounded-xl hover:from-pink-600 hover:to-blue-600 transform hover:-translate-y-1 transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                  Connexion en cours...
                </div>
              ) : (
                <div className="flex items-center">
                  <LogIn className="w-5 h-5 mr-3" />
                  Se connecter
                </div>
              )}
            </button>

            {/* Lien inscription */}
            <div className="text-center pt-4 border-t border-gray-600">
              <p className="text-sm text-gray-400">
                Nouveau sur CV Screening Pro ?{' '}
                <Link to="/signup" className="font-medium text-blue-400 hover:text-blue-300 transition-colors">
                  Créer un compte entreprise
                </Link>
              </p>
            </div>
          </form>
        </div>

        {/* Debug Info (à supprimer en production) */}
        {process.env.NODE_ENV === 'development' && (
          <div className="glass-card p-4">
            <p className="text-xs text-gray-500 mb-2">Debug - Services:</p>
            <pre className="text-xs text-gray-400 overflow-auto max-h-32">
              {debugInfo}
            </pre>
          </div>
        )}
      </div>
    </div>
  )
}